import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import { respondToNegotiationInvite } from '@/redux/asyncThunks/negotiationWebsocket'
import { NegotiationType } from '@/redux/types/negotiationWebsocket'
import { Status } from '@/redux/types/room'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import { hasLeftGroupChat } from '@/utils/groupChatMembers'

const NegotiationInvitationList: React.FC = () => {
  const { negotiations } = useAppSelector((state) => state.negotiationWS)
  const { user } = useEffectiveUser()
  const dispatch = useAppDispatch()

  const pendingInvitations = useMemo(() => {
    return negotiations.filter((negotiation) => {
      if (negotiation.negotiation_type === NegotiationType.PUBLIC_ROOM)
        return false

      const currentUserMember = negotiation.members.find(
        (member) => member.email === user?.email
      )

      if (!currentUserMember || currentUserMember.status !== Status.Pending)
        return false

      // Group chats: any pending own row is an open invite, regardless of
      // how many members the chat has.
      if (negotiation.negotiation_type === NegotiationType.GROUP_ROOM) {
        return true
      }

      if (negotiation.member_count !== 2) return false

      const ownerMember = negotiation.members.find(
        (member) => member.status === Status.Owner
      )
      const pendingMember = negotiation.members.find(
        (member) => member.status === Status.Pending
      )

      return ownerMember && pendingMember && pendingMember.email === user?.email
    })
  }, [negotiations, user?.email])

  const handleResponse = (negotiationId: string, accepted: boolean) => {
    dispatch(respondToNegotiationInvite({ negotiationId, accepted }))
  }

  if (pendingInvitations.length === 0) return null

  return (
    <div className='w-full max-w-full'>
      <div className='flex w-full max-w-full flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm'>
        {pendingInvitations.map((invite) => {
          const inviter = invite.members.find(
            (member) => member.status === Status.Owner
          )
          const isGroup =
            invite.negotiation_type === NegotiationType.GROUP_ROOM
          const otherNames = isGroup
            ? invite.members
                .filter(
                  (member) =>
                    member.email !== user?.email &&
                    member.email !== inviter?.email &&
                    !hasLeftGroupChat(member)
                )
                .map(
                  (member) =>
                    member.name?.split(' ')[0] || member.email?.split('@')[0]
                )
            : []

          return (
            <div key={invite.negotiationId} className='flex flex-col space-y-2'>
              <p className='text-sm break-words'>
                {isGroup
                  ? `${inviter?.name || inviter?.email} invited you to a group chat${
                      otherNames.length > 0
                        ? ` with ${otherNames.join(', ')}`
                        : ''
                    }`
                  : `${inviter?.name || inviter?.email} is inviting you for a negotiation`}
              </p>
              <div className='flex items-center justify-between gap-2'>
                <Button
                  size='lg'
                  className='bg-dark-blue text-white flex-1'
                  onClick={() => handleResponse(invite.negotiationId, true)}
                >
                  Accept
                </Button>
                <Button
                  size='lg'
                  className='bg-red-500 text-white flex-1'
                  onClick={() => handleResponse(invite.negotiationId, false)}
                >
                  Reject
                </Button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default NegotiationInvitationList
